import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'pt';

  const footerNav = [
    { label: t('nav_spreadsheet'), href: `/${currentLang === 'pt' ? '' : currentLang + '/'}cssbuy-spreadsheet/` },
    { label: t('nav_categories'), href: `/${currentLang === 'pt' ? '' : currentLang + '/'}categories/` },
    { label: t('nav_guides'), href: `/${currentLang === 'pt' ? '' : currentLang + '/'}guides/` },
    { label: t('nav_shipping'), href: `/${currentLang === 'pt' ? '' : currentLang + '/'}shipping/` },
    { label: t('nav_reviews'), href: `/${currentLang === 'pt' ? '' : currentLang + '/'}reviews/` },
  ];

  const legalLinks = [
    { label: t('footer_about'), href: '/about/' },
    { label: t('footer_safety'), href: '/safety/' },
    { label: t('footer_disclaimer'), href: '/disclaimer/' },
    { label: t('footer_privacy'), href: '/privacy/' },
    { label: t('footer_terms'), href: '/terms/' },
  ];

  const languages = [
    { code: 'pt', label: 'Português', path: '/' },
    { code: 'de', label: 'Deutsch', path: '/de/' },
    { code: 'en', label: 'English', path: '/en/' },
  ];

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    const target = lang === 'pt' ? '/' : `/${lang}/`;
    navigate(target);
  };

  return (
    <footer className="bg-secondary-950 text-white">
      <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a href="/" className="inline-block">
              <span className="font-heading text-2xl font-bold tracking-tight">
                CSSBuy<span className="text-primary-400">BR</span>
              </span>
            </a>
            <p className="mt-4 text-sm text-secondary-200 leading-relaxed">
              {t('disclaimer_short')}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 rounded-full bg-secondary-800 flex items-center justify-center hover:bg-primary-500 transition-colors">
                <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-sm" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-heading text-base font-semibold mb-4 text-white">{t('footer_resources')}</h3>
            <ul className="space-y-2.5">
              {footerNav.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-sm text-secondary-200 hover:text-primary-300 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-heading text-base font-semibold mb-4 text-white">Legal</h3>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-sm text-secondary-200 hover:text-primary-300 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Language */}
          <div>
            <h3 className="font-heading text-base font-semibold mb-4 text-white">{t('language')}</h3>
            <div className="space-y-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => switchLanguage(lang.code)}
                  className={`block text-sm transition-colors ${
                    currentLang === lang.code
                      ? 'text-primary-400 font-medium'
                      : 'text-secondary-200 hover:text-primary-300'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Disclaimer Banner */}
        <div className="mt-12 pt-8 border-t border-secondary-800">
          <p className="text-xs text-secondary-400 leading-relaxed max-w-4xl">
            {t('disclaimer_full')}
          </p>
        </div>

        {/* Copyright */}
        <div className="mt-6 pt-6 border-t border-secondary-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-secondary-400">
            {t('footer_copyright')}
          </p>
          <p className="text-xs text-secondary-400">
            {t('updated')}: 2026-08-30
          </p>
        </div>
      </div>
    </footer>
  );
}